import React from "react";
import { MdCheckCircle, MdContentCopy, MdDesktopWindows, MdErrorOutline, MdFormatAlignLeft, MdRestore, MdSmartphone, MdWarningAmber } from "react-icons/md";

export const copyText = async (value) => {
  if (navigator.clipboard?.writeText) return navigator.clipboard.writeText(value);
  const area = document.createElement("textarea");
  area.value = value; document.body.appendChild(area); area.select();
  document.execCommand("copy"); area.remove();
  return undefined;
};

// Errors block a save, warnings only inform.
export function IssueList({ errors = [], warnings = [], t }) {
  if (!errors.length && !warnings.length) return null;
  return <ul className="eck-issues">
    {errors.map((issue, index) => <li key={`e${index}`} className="is-error"><MdErrorOutline aria-hidden="true" /> {issue.message || issue}</li>)}
    {warnings.map((issue, index) => <li key={`w${index}`} className="is-warning"><MdWarningAmber aria-hidden="true" /> {issue.message || issue}</li>)}
  </ul>;
}

export function OkLine({ children }) {
  return <p className="eck-ok"><MdCheckCircle aria-hidden="true" /> {children}</p>;
}

// Monospaced JSON editor. The last VALID document stays in use (form,
// preview) while the text has a syntax error.
export function JsonEditor({ text, error, onChange, onFormat, onReset, onCopy, onCopyForTranslation, canReset, t, label }) {
  const lines = String(text || "").split("\n");
  return <div className="eck-code">
    <div className="eck-toolbar">
      <button type="button" className="eck-btn" onClick={onFormat} disabled={Boolean(error)}><MdFormatAlignLeft aria-hidden="true" /> {t.formatJson}</button>
      <button type="button" className="eck-btn" onClick={onCopy}><MdContentCopy aria-hidden="true" /> {t.copyJson}</button>
      {onCopyForTranslation && <button type="button" className="eck-btn" onClick={onCopyForTranslation} disabled={Boolean(error)}><MdContentCopy aria-hidden="true" /> {t.copyForTranslation}</button>}
      <button type="button" className="eck-btn" onClick={onReset} disabled={!canReset}><MdRestore aria-hidden="true" /> {t.resetSaved}</button>
      <span className="eck-toolbar__spacer" />
      {error ? <span className="eck-pill is-bad">{t.jsonInvalid}</span> : <span className="eck-pill is-ok">{t.jsonValid}</span>}
    </div>
    {error && <div className="eck-alert" role="alert">
      <strong>{error.message}</strong>
      {error.line && <pre className="eck-error-line">{`${error.line} | ${lines[error.line - 1] ?? ""}\n${" ".repeat(String(error.line).length + 3 + Math.max(0, (error.column || 1) - 1))}^`}</pre>}
      <div className="eck-hint">{t.jsonKeepsLastValid}</div>
    </div>}
    <textarea className="eck-code__area" aria-label={label || t.jsonTab} spellCheck={false} value={text} onChange={(e) => onChange(e.target.value)} />
  </div>;
}

const CATEGORY_ORDER = ["protected_system_token", "dynamic_html", "translatable_content", "user_data", "system_data", "unknown"];

// Detected tokens of an HTML template, by category, with what changed since
// the saved version.
export function TokenInspector({ diff, t, onInsert }) {
  if (!diff) return null;
  const groups = CATEGORY_ORDER.map((category) => [category, diff.tokens.filter((token) => token.category === category)]).filter(([, list]) => list.length);
  return <div className="eck-tokens">
    <div className="eck-token-stats">
      <span><strong>{diff.detected}</strong> {t.detectedTokens}</span>
      <span className={diff.added.length ? "is-info" : ""}><strong>{diff.added.length}</strong> {t.newTokens}</span>
      <span className={diff.removed.length ? "is-warning" : ""}><strong>{diff.removed.length}</strong> {t.removedTokens}</span>
      <span className={diff.missingProtected.length ? "is-error" : "is-ok"}><strong>{diff.missingProtected.length}</strong> {t.missingProtected}</span>
    </div>
    {diff.missingProtected.length > 0 && <p className="eck-field__error">{t.missingProtectedList} {diff.missingProtected.map((name) => `{{${name}}}`).join(", ")}</p>}
    {diff.removed.length > 0 && <p className="eck-hint">{t.removedList} {diff.removed.map((name) => `{{${name}}}`).join(", ")}</p>}
    {groups.map(([category, list]) => <div key={category} className="eck-token-group">
      <div className="eck-token-group__title">{t.categories?.[category] || category}</div>
      <div className="eck-token-group__list">
        {list.map((token) => <button key={token.name} type="button" className={`eck-token is-${category}${diff.added.includes(token.name) ? " is-new" : ""}`} title={`${t.line} ${token.line} · ×${token.count}`} onClick={() => onInsert?.(token.name)}>{`{{${token.name}}}`}</button>)}
      </div>
    </div>)}
  </div>;
}

export function DeviceToggle({ device, setDevice, t }) {
  return <div className="eck-segment" role="group" aria-label={`${t.desktop} / ${t.mobile}`}>
    <button type="button" aria-pressed={device === "desktop"} className={device === "desktop" ? "is-active" : ""} onClick={() => setDevice("desktop")}><MdDesktopWindows aria-hidden="true" /> {t.desktop}</button>
    <button type="button" aria-pressed={device === "mobile"} className={device === "mobile" ? "is-active" : ""} onClick={() => setDevice("mobile")}><MdSmartphone aria-hidden="true" /> {t.mobile}</button>
  </div>;
}

// Sandboxed: the email HTML is shown, never executed with this app's origin.
export function EmailPreviewFrame({ html, device = "desktop", title }) {
  return <div className={`eck-frame eck-frame--${device}`}>
    <iframe title={title} sandbox="" srcDoc={html || ""} />
  </div>;
}
